import { ChangeDetectorRef, Component, ViewChild } from "@angular/core";
import { GroupService } from "@app/services/groups/group.service";
import { ResponsiveService } from "@app/services/responsive.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { IGroupWithIds } from "../_models/IGroupWithIds";
import { GroupEditorComponent } from "../group-editor/group-editor.component";
import { GroupHierarchyComponent2 } from "../group-hierarchy/group-hierarchy.component";
import { GroupsHierarchyHelper } from "../utils/groups-hierarchy-helper";
import './groups-list.component.scss';

@UntilDestroy()
@Component({
    selector: 'groups-list',
    template: require('./groups-list.component.html')
})
export class GroupsListComponent {
    hierarchy: IGroupWithIds[] = [];
    allGroups: IGroupWithIds[] = [];

    @ViewChild(GroupEditorComponent) groupEditor: GroupEditorComponent;
    @ViewChild(GroupHierarchyComponent2) groupHierarchy: GroupHierarchyComponent2;

    selectedGroup: IGroupWithIds;

    showSidebar: boolean;
    loading: boolean;
    savingGroup: boolean;

    constructor(
        private readonly groupService: GroupService,
        private readonly responsiveService: ResponsiveService,
        private readonly translationService: TranslationService,
        private readonly changeDetector: ChangeDetectorRef) { }

    ngOnInit(): void {
        this.getData();
        this.determineScreenWidth()
    }

    onGroupSelected(group: IGroupWithIds): void {
        if (this.selectedGroup && 
            this.groupEditor?.editorHasChanges && 
            !window.confirm(this.translationService.t('groupList.ConfirmCancel'))) {
            return;
        }

        this.clearSelectedGroup();
        this.changeDetector.detectChanges();
        this.selectedGroup = GroupsHierarchyHelper.find(g => g.Id === group.Id, this.hierarchy);
    }

    clearSelectedGroup(): void {
        this.selectedGroup = null;
    }

    onNewGroupCreated(group: IGroupWithIds): void {
        this.selectedGroup = GroupsHierarchyHelper.find(g => g.Id === group.Id, this.hierarchy) || group;
    }

    addNewGroup(): void {
        this.selectedGroup = this.groupService.getNewGroupWithIds();
    }

    onSavingGroup(): void {
        this.savingGroup = true;
    }

    async onGroupSaved(group: IGroupWithIds): Promise<void> {
        await this.getData(true, false);

        this.selectedGroup = !!group 
            ? GroupsHierarchyHelper.find(g => g.Id === group.Id, this.hierarchy) 
            : null;
    }

    private async getData(invalidateCache = false, showLoading = true): Promise<void> {
        this.loading = showLoading;
        this.hierarchy = await this.groupService.getGroupsHierarchy(invalidateCache);
        this.allGroups = await this.groupService.getGroupsWithIds();
        this.loading = false;
        this.savingGroup = false;

        this.clearSelectedGroup();
    }

    private determineScreenWidth(): void {
        this.responsiveService.currentWindowWidth$.pipe(untilDestroyed(this)).subscribe({
            next: width => this.showSidebar = width < 1100
        })
    }
}
